import React, { ReactNode, useEffect } from 'react';
import { useVersion } from '@/contexts/VersionContext';
import { useAppSelector } from '@/hooks/useAppSelector';

interface VersionAwareWrapperProps {
  children: ReactNode;
  componentName: string; // For logging purposes
}

/**
 * A wrapper component that provides version context to its children
 * This component can be used to wrap any tab content that needs to be aware of the selected version
 * It gets the selected version from Redux via the VersionContext
 */
const VersionAwareWrapper: React.FC<VersionAwareWrapperProps> = ({ 
  children, 
  componentName 
}) => {
  // Get version from context and versions from Redux
  const { selectedVersion } = useVersion();
  const versions = useAppSelector(state => state.versions.versions);
  
  // Log when the selected version changes
  useEffect(() => {
    if (selectedVersion) {
      console.log(`🔄 [${componentName}] Version changed to: ${selectedVersion}`);
    }
  }, [selectedVersion, componentName]);

  // Clone children and pass selectedVersion as a prop
  const childrenWithProps = React.Children.map(children, child => {
    // Check if child is a valid React element
    if (React.isValidElement(child)) {
      // Clone the child with the selectedVersion prop
      return React.cloneElement(child, { 
        selectedVersion,
        versions
      });
    }
    return child;
  });

  return (
    <div className="version-aware-wrapper">
      {childrenWithProps}
    </div>
  );
};

export default VersionAwareWrapper;
